/**
 * Staff + doctors mock adapter.
 *
 * Mirrors `staffApi` / `doctorsApi` in `../staff.ts` so the
 * `MOCKS_ENABLED ? mock : real` swap is a no-op for callers. Seeds an
 * in-memory clinic team so `/settings/users` (+ /new, /:id) and
 * `/settings/doctors` are click-through with no backend / tenant header.
 */
import { delay } from "./registry";
import type { PageResponse } from "../../types/patient";
import type {
  DoctorLicenseCreateRequest,
  DoctorLicenseResponse,
  DoctorLicenseUpdateRequest,
  DoctorResponse,
  DoctorServiceRef,
  DoctorSummaryResponse,
  DoctorUpdateRequest,
  InviteStatus,
  StaffCreateRequest,
  StaffInviteRequest,
  StaffInviteResponse,
  StaffInviteSummary,
  StaffResponse,
  StaffRole,
  StaffStatus,
  StaffSummaryResponse,
  StaffUpdateRequest,
} from "../../types/staff";
import type { StaffListFilter } from "../staff";

/* ------------------------------------------------------------------ */
/* Seed data                                                          */
/* ------------------------------------------------------------------ */

const now = Date.now();
const iso = (offsetDays = 0) =>
  new Date(now - offsetDays * 86_400_000).toISOString();

// [fullName, role, status, email, phone]
const SEED: [string, StaffRole, StaffStatus, string, string][] = [
  ["Nguyễn Văn An", "OWNER", "ACTIVE", "an.nguyen@clinic.vn", "+84901000001"],
  ["Trần Thị Bình", "MANAGER", "ACTIVE", "binh.tran@clinic.vn", "+84901000002"],
  ["BS. Lê Quang Vinh", "DOCTOR", "ACTIVE", "vinh.le@clinic.vn", "+84901000003"],
  ["BS. Đỗ Văn Giang", "DOCTOR", "ACTIVE", "giang.do@clinic.vn", "+84901000004"],
  ["ThS.BS. Nguyễn Thị Hồng", "DOCTOR", "LEAVE", "hong.nguyen@clinic.vn", "+84901000005"],
  ["Phạm Thị Mai", "NURSE", "ACTIVE", "mai.pham@clinic.vn", "+84901000006"],
  ["Hoàng Thị Lan", "NURSE", "ACTIVE", "lan.hoang@clinic.vn", "+84901000007"],
  ["Vũ Thị Ngọc", "RECEPTIONIST", "ACTIVE", "ngoc.vu@clinic.vn", "+84901000008"],
  ["Bùi Văn Tâm", "RECEPTIONIST", "TERMINATED", "tam.bui@clinic.vn", "+84901000009"],
  ["Đặng Thị Thu", "OTHER", "ACTIVE", "thu.dang@clinic.vn", "+84901000010"],
];

const STAFF: StaffResponse[] = SEED.map(
  ([fullName, staffRole, status, email, phone], i) => ({
    id: `stf-${String(i + 1).padStart(4, "0")}`,
    userId: status === "ACTIVE" ? `usr-${String(i + 1).padStart(4, "0")}` : null,
    fullName,
    staffRole,
    status,
    email,
    phone,
    primaryBranchId: "br-main",
    updatedAt: iso(i),
    birthDate: `19${70 + i}-0${(i % 9) + 1}-1${i % 9}`,
    nationalId: `0${79_000_000_000 + i * 137}`,
    addressLine: `Số ${5 + i} đường Nguyễn Huệ, Q.1`,
    notes: null,
    avatarUrl: null,
    fax: null,
    country: "Việt Nam",
    facebook: null,
    twitter: null,
    linkedin: null,
    createdAt: iso(120 + i),
    version: 1,
  })
);

// Doctors derived from DOCTOR-role staff.
const DOCTORS: DoctorResponse[] = STAFF.filter(
  (s) => s.staffRole === "DOCTOR"
).map((s, i) => ({
  id: `doc-${String(i + 1).padStart(4, "0")}`,
  tenantId: "tnt-demo",
  staffProfileId: s.id,
  title: i === 2 ? "ThS.BS." : "BS.",
  specialty: ["Nội tổng quát", "Tiêu hoá", "Nhi khoa"][i] ?? "Nội tổng quát",
  departmentId: null,
  bio: null,
  signatureImageUrl: null,
  nationalRxLinkCode: null,
  nationalRxClinicCode: null,
  nationalRxPrescriptionType: null,
  prescriber: true,
  version: 1,
}));

const DOCTOR_SERVICES: Record<string, DoctorServiceRef[]> = {};

const LICENSES: Record<string, DoctorLicenseResponse[]> = {};
DOCTORS.forEach((d, i) => {
  const expiresInDays = i === 0 ? 30 : 400; // first one expiring soon
  LICENSES[d.id] = [
    {
      id: `lic-${d.id}`,
      doctorProfileId: d.id,
      licenseNumber: `CCHN-${10000 + i}`,
      issuedBy: "Sở Y tế TP.HCM",
      issuedAt: iso(800),
      validUntil: iso(-expiresInDays),
      verifiedAt: iso(700),
      verifiedVia: "Cổng Bộ Y tế",
      notes: null,
      active: true,
      expired: false,
      createdAt: iso(800),
      updatedAt: iso(700),
    },
  ];
});

const INVITES: StaffInviteSummary[] = [
  {
    id: "inv-0001",
    staffProfileId: "stf-0011",
    email: "newdoctor@clinic.vn",
    roleCodes: ["DOCTOR"],
    status: "SENT",
    expiresAt: iso(-7),
    sentAt: iso(1),
    acceptedAt: null,
    revokedAt: null,
  },
  {
    id: "inv-0002",
    staffProfileId: "stf-0012",
    email: "newnurse@clinic.vn",
    roleCodes: ["NURSE"],
    status: "ACCEPTED",
    expiresAt: iso(-3),
    sentAt: iso(10),
    acceptedAt: iso(8),
    revokedAt: null,
  },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

function toSummary(s: StaffResponse): StaffSummaryResponse {
  return {
    id: s.id,
    userId: s.userId,
    fullName: s.fullName,
    staffRole: s.staffRole,
    status: s.status,
    email: s.email,
    phone: s.phone,
    primaryBranchId: s.primaryBranchId,
    updatedAt: s.updatedAt,
  };
}

function findStaff(id: string): StaffResponse {
  const s = STAFF.find((x) => x.id === id);
  if (!s) throw new Error(`Mock: nhân sự ${id} không tồn tại`);
  return s;
}

function paginate<T>(rows: T[], page = 0, size = 20): PageResponse<T> {
  return {
    content: rows.slice(page * size, page * size + size),
    totalElements: rows.length,
    totalPages: Math.max(1, Math.ceil(rows.length / size)),
    number: page,
    size,
  };
}

function setStatus(id: string, status: StaffStatus): Promise<void> {
  const s = findStaff(id);
  s.status = status;
  s.updatedAt = new Date().toISOString();
  return delay(undefined);
}

/* ------------------------------------------------------------------ */
/* Adapters                                                           */
/* ------------------------------------------------------------------ */

export const mockStaff = {
  list(
    filter: StaffListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<StaffSummaryResponse>> {
    let rows = STAFF;
    if (filter?.q) {
      const n = filter.q.trim().toLowerCase();
      rows = rows.filter(
        (s) =>
          s.fullName.toLowerCase().includes(n) ||
          (s.email ?? "").toLowerCase().includes(n) ||
          (s.phone ?? "").includes(n)
      );
    }
    if (filter?.staffRole)
      rows = rows.filter((s) => s.staffRole === filter.staffRole);
    if (filter?.status) rows = rows.filter((s) => s.status === filter.status);
    const sorted = rows
      .slice()
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map(toSummary);
    return delay(paginate(sorted, pageable.page ?? 0, pageable.size ?? 20));
  },

  get(id: string): Promise<StaffResponse> {
    return delay({ ...findStaff(id) });
  },

  create(req: StaffCreateRequest): Promise<StaffResponse> {
    const ts = new Date().toISOString();
    const s: StaffResponse = {
      id: `stf-${Math.random().toString(36).slice(2, 8)}`,
      userId: null,
      fullName: req.fullName,
      staffRole: req.staffRole,
      status: "ACTIVE",
      email: req.email ?? null,
      phone: req.phone ?? null,
      primaryBranchId: req.primaryBranchId ?? "br-main",
      updatedAt: ts,
      birthDate: req.birthDate ?? null,
      nationalId: req.nationalId ?? null,
      addressLine: req.addressLine ?? null,
      notes: req.notes ?? null,
      avatarUrl: req.avatarUrl ?? null,
      fax: req.fax ?? null,
      country: req.country ?? null,
      facebook: req.facebook ?? null,
      twitter: req.twitter ?? null,
      linkedin: req.linkedin ?? null,
      createdAt: ts,
      version: 1,
    };
    STAFF.unshift(s);
    return delay({ ...s });
  },

  update(id: string, req: StaffUpdateRequest): Promise<StaffResponse> {
    const s = findStaff(id);
    Object.assign(s, {
      fullName: req.fullName ?? s.fullName,
      staffRole: req.staffRole ?? s.staffRole,
      email: req.email ?? s.email,
      phone: req.phone ?? s.phone,
      birthDate: req.birthDate ?? s.birthDate,
      nationalId: req.nationalId ?? s.nationalId,
      addressLine: req.addressLine ?? s.addressLine,
      notes: req.notes ?? s.notes,
      primaryBranchId: req.primaryBranchId ?? s.primaryBranchId,
      updatedAt: new Date().toISOString(),
      version: s.version + 1,
    });
    return delay({ ...s });
  },

  leave: (id: string) => setStatus(id, "LEAVE"),
  returnFromLeave: (id: string) => setStatus(id, "ACTIVE"),
  terminate: (id: string) => setStatus(id, "TERMINATED"),
  reactivate: (id: string) => setStatus(id, "ACTIVE"),
  softDelete(id: string): Promise<void> {
    const i = STAFF.findIndex((x) => x.id === id);
    if (i >= 0) STAFF.splice(i, 1);
    return delay(undefined);
  },
  remove(id: string): Promise<void> {
    const i = STAFF.findIndex((x) => x.id === id);
    if (i >= 0) STAFF.splice(i, 1);
    return delay(undefined);
  },
  restore: (id: string) => setStatus(id, "ACTIVE"),

  invite(req: StaffInviteRequest): Promise<StaffInviteResponse> {
    const inviteId = `inv-${Math.random().toString(36).slice(2, 8)}`;
    const staffProfileId = `stf-${Math.random().toString(36).slice(2, 8)}`;
    INVITES.unshift({
      id: inviteId,
      staffProfileId,
      email: req.email,
      roleCodes: req.roleCodes,
      status: "SENT",
      expiresAt: iso(-7),
      sentAt: new Date().toISOString(),
      acceptedAt: null,
      revokedAt: null,
    });
    return delay({
      staffProfileId,
      inviteId,
      doctorProfileId: req.asDoctor ? `doc-${inviteId}` : null,
      inviteLink: `https://app.addy.vn/invite/${inviteId}`,
      email: req.email,
      roleCodes: req.roleCodes,
      status: "SENT",
      expiresAt: iso(-7),
    });
  },
  listInvites(status: InviteStatus = "SENT"): Promise<StaffInviteSummary[]> {
    return delay(INVITES.filter((i) => i.status === status));
  },
  resendInvite(id: string): Promise<StaffInviteResponse> {
    const inv = INVITES.find((i) => i.id === id);
    return delay({
      staffProfileId: inv?.staffProfileId ?? "stf-unknown",
      inviteId: id,
      doctorProfileId: null,
      inviteLink: `https://app.addy.vn/invite/${id}`,
      email: inv?.email ?? "unknown@clinic.vn",
      roleCodes: inv?.roleCodes ?? [],
      status: "SENT",
      expiresAt: iso(-7),
    });
  },
  revokeInvite(id: string): Promise<void> {
    const inv = INVITES.find((i) => i.id === id);
    if (inv) {
      inv.status = "REVOKED";
      inv.revokedAt = new Date().toISOString();
    }
    return delay(undefined);
  },
};

export const mockDoctors = {
  list(
    pageable: { page?: number; size?: number; sort?: string } = {}
  ): Promise<PageResponse<DoctorSummaryResponse>> {
    const rows: DoctorSummaryResponse[] = DOCTORS.map((d) => ({
      id: d.id,
      staffProfileId: d.staffProfileId,
      fullName: STAFF.find((s) => s.id === d.staffProfileId)?.fullName ?? null,
      title: d.title,
      specialty: d.specialty,
      prescriber: d.prescriber,
    }));
    return delay(paginate(rows, pageable.page ?? 0, pageable.size ?? 20));
  },
  get(id: string): Promise<DoctorResponse> {
    const d = DOCTORS.find((x) => x.id === id || x.staffProfileId === id);
    if (!d) throw new Error(`Mock: bác sĩ ${id} không tồn tại`);
    return delay({ ...d });
  },
  update(id: string, req: DoctorUpdateRequest): Promise<DoctorResponse> {
    const d = DOCTORS.find((x) => x.id === id || x.staffProfileId === id);
    if (!d) throw new Error(`Mock: bác sĩ ${id} không tồn tại`);
    d.title = req.title ?? null;
    d.specialty = req.specialty;
    d.departmentId = req.departmentId ?? null;
    if (req.bio !== undefined) d.bio = req.bio ?? null;
    return delay({ ...d });
  },
  setPrescriber(id: string, on: boolean): Promise<void> {
    const d = DOCTORS.find((x) => x.id === id || x.staffProfileId === id);
    if (d) d.prescriber = on;
    return delay(undefined);
  },
  signatureUrl(_id: string): Promise<string | null> {
    return delay(null);
  },
  uploadSignature(_id: string, _file: File): Promise<string> {
    return delay("mock://signature");
  },
  listServices(doctorId: string): Promise<DoctorServiceRef[]> {
    return delay((DOCTOR_SERVICES[doctorId] ?? []).map((s) => ({ ...s })));
  },
  setServices(doctorId: string, serviceIds: string[]): Promise<void> {
    DOCTOR_SERVICES[doctorId] = serviceIds.map((id) => ({
      serviceId: id,
      code: id,
      name: id,
    }));
    return delay(undefined);
  },
  listLicenses(doctorId: string): Promise<DoctorLicenseResponse[]> {
    return delay((LICENSES[doctorId] ?? []).map((l) => ({ ...l })));
  },
  createLicense(
    doctorId: string,
    req: DoctorLicenseCreateRequest
  ): Promise<void> {
    const ts = new Date().toISOString();
    (LICENSES[doctorId] ??= []).push({
      id: `lic-${Math.random().toString(36).slice(2, 8)}`,
      doctorProfileId: doctorId,
      licenseNumber: req.licenseNumber,
      issuedBy: req.issuedBy ?? null,
      issuedAt: req.issuedAt,
      validUntil: req.validUntil ?? null,
      verifiedAt: null,
      verifiedVia: null,
      notes: req.notes ?? null,
      active: true,
      expired: false,
      createdAt: ts,
      updatedAt: ts,
    });
    return delay(undefined);
  },
  updateLicense(
    doctorId: string,
    licenseId: string,
    req: DoctorLicenseUpdateRequest
  ): Promise<void> {
    const l = (LICENSES[doctorId] ?? []).find((x) => x.id === licenseId);
    if (l) Object.assign(l, req, { updatedAt: new Date().toISOString() });
    return delay(undefined);
  },
  verifyLicense(
    doctorId: string,
    licenseId: string,
    via: string
  ): Promise<void> {
    const l = (LICENSES[doctorId] ?? []).find((x) => x.id === licenseId);
    if (l) {
      l.verifiedAt = new Date().toISOString();
      l.verifiedVia = via;
    }
    return delay(undefined);
  },
  deactivateLicense(doctorId: string, licenseId: string): Promise<void> {
    const l = (LICENSES[doctorId] ?? []).find((x) => x.id === licenseId);
    if (l) l.active = false;
    return delay(undefined);
  },
  expiringLicenses(days = 60): Promise<DoctorLicenseResponse[]> {
    const cutoff = now + days * 86_400_000;
    const out: DoctorLicenseResponse[] = [];
    for (const list of Object.values(LICENSES)) {
      for (const l of list) {
        if (
          l.active &&
          l.validUntil &&
          new Date(l.validUntil).getTime() <= cutoff
        ) {
          out.push({ ...l });
        }
      }
    }
    return delay(out);
  },
};
